/* eslint-disable max-lines */
import { useEffect, useRef, useState, useMemo, createRef } from 'react';

import {
  Chip,
  IconButton,
  Input,
  InputAdornment,
  InputLabel,
  TableCell,
  TableHead as MuiTableHead,
  TableRow,
  TableSortLabel,
  TextField,
 Autocomplete } from '@mui/material';
import { FilterList as FilterIcon, Search as SearchIcon } from '@mui/icons-material';
import clsx from 'clsx';
import isEqual from 'lodash/isEqual';

import { TableFilterItem } from '..';
import { TableColumn, TableSearchTypes, TableSuggestions } from '../Table.types';
import { ColumnFilterPopper } from './components/ColumnFilterPopper';
import { useTableHeadStyles } from './TableHead.styles';
import { AlignTypes } from 'models/commonTypes';

// Iterate over columns finding all filter conditions with a value set
const getInitialFilters = (columns: TableColumn[]): Array<TableFilterItem> => {
  const initialFilters = [];
  columns.forEach(({ id, alias, filters }) => {
    filters?.forEach(filter => {
      const conditions = filter.conditions.filter(condition => condition.value);

      if (conditions.length > 0) {
        if (filter.type === 'dual-multiselect' && filter.columnIds) {
          conditions.forEach(condition => {
            if (filter.columnIds.includes(condition.key)) {
              initialFilters.push({
                columnId: condition.key,
                condition: 'in',
                value: condition.value,
              });
            }
          });
        } else {
          conditions.forEach(condition => {
            initialFilters.push({
              columnId: alias ?? id,
              condition: condition.key,
              value: condition.value,
            });
          });
        }
      }
    });
  });

  return initialFilters;
};

export interface TableHeadProps {
  actionCell: boolean;
  cellPadding?: string | number;
  columns: Array<TableColumn>;
  controlledFilters?: Array<TableFilterItem>;
  searchType?: TableSearchTypes;
  suggestions?: TableSuggestions;
  localStorageKey?: string;
  onFilterBy: (value: any) => void;
  onRequestSort: (value: any) => void;
  onSearch: (value: any) => void;
}
const DEFAULT_MIN_WIDTH_CELL = 70;
const DEFAULT_MAX_WIDTH_CELL = 800;
export const TableHead = ({
  actionCell,
  cellPadding = '5px 10px',
  columns,
  controlledFilters,
  onFilterBy,
  onRequestSort,
  onSearch,
  searchType = '',
  suggestions = [],
  localStorageKey,
}: TableHeadProps) => {
  const classes = useTableHeadStyles({ cellPadding });
  const [columnRefs, setColumnRefs] = useState(columns.map(() => createRef<HTMLDivElement>()));
  const isResizing = useRef(-1);
  const [open, setOpen] = useState(false);
  const anchorRefs = useRef([]);
  const [activeColumn, setActiveColumn] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [currentFilters, setCurrentFilters] = useState(getInitialFilters(columns));
  const [appliedOrders, setAppliedOrders] = useState(new Map());
  // Holds the filter snapshot the user just committed (via the chip X or the popper).
  // The "reset from columns" effect must skip while this is set, because the column
  // prop is derived from the URL/storedFilters and lags behind by 1+ renders. We only
  // clear it once `columns` actually reflects the user's change, so any extra
  // intermediate renders during URL propagation can't re-add a chip the user just removed.
  const pendingUserFiltersRef = useRef<TableFilterItem[] | null>(null);

  const initialOrders = useMemo(
    () =>
      columns.reduce((orders, column) => {
        if (column.order) {
          orders.set(column.id, column.order);
        }
        return orders;
      }, new Map()),
    [columns],
  );

  useEffect(() => {
    if (!controlledFilters) {
      return;
    }

    setCurrentFilters(controlledFilters);
  }, [controlledFilters]);

  useEffect(() => {
    const initialFilters = getInitialFilters(columns);

    // While a user-initiated change is still propagating through the URL/storedFilters,
    // hold off on syncing from columns - the column values lag behind by 1+ renders and
    // would otherwise re-add a chip the user just removed. Only clear the snapshot once
    // columns have caught up to the user's last committed filter set.
    if (pendingUserFiltersRef.current !== null) {
      if (isEqual(initialFilters, pendingUserFiltersRef.current)) {
        pendingUserFiltersRef.current = null;
      }
      return;
    }

    // No pending user change - sync from columns when they meaningfully differ
    // (e.g. external URL change via back/forward, or initial column hydration).
    if (currentFilters.length === 0 && initialFilters.length > 0) {
      setCurrentFilters(initialFilters);
    }
  }, [columns, currentFilters]);

  useEffect(() => {
    setAppliedOrders(initialOrders);
  }, [initialOrders]);

  useEffect(() => {
    setColumnRefs(columns.map((_col, index) => columnRefs[index] || createRef<HTMLDivElement>()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns]);

  useEffect(() => {
    anchorRefs.current = anchorRefs.current.slice(0, columns.length);
  }, [columns]);

  useEffect(() => {
    loadColumnInfoLocalStorage();
    document.addEventListener('mousemove', handleOnMouseMove);
    document.addEventListener('mouseup', handleOnMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleOnMouseMove);
      document.removeEventListener('mouseup', handleOnMouseUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = event => {
    const { current } = anchorRefs;

    const activeCellIndex = columns.findIndex(col => col.id === activeColumn?.id);

    if (current[activeCellIndex]?.contains(event.target)) {
      return;
    }

    setOpen(false);
  };

  /**
   * Updates the filters state with an empty value
   * @param {string} columnId
   * @param {string} name
   */
  const onDiscardFilter = (columnId, condition) => {
    const cloned = [...currentFilters];
    const removedFilterIndex = currentFilters.findIndex(
      f => f.columnId === columnId && f.condition === condition,
    );
    if (removedFilterIndex > -1) {
      cloned.splice(removedFilterIndex, 1);

      pendingUserFiltersRef.current = cloned;
      setCurrentFilters(cloned);
      onFilterBy(cloned);

      const isDualMultiselect = columns.some(col =>
        col.filters?.some(
          filter => filter.type === 'dual-multiselect' && filter.columnIds?.includes(columnId),
        ),
      );

      if (isDualMultiselect) {
        const column = columns.find(col =>
          col.filters?.some(
            filter => filter.type === 'dual-multiselect' && filter.columnIds?.includes(columnId),
          ),
        );
        if (column) {
          const dualFilter = column.filters?.find(f => f.type === 'dual-multiselect');
          if (dualFilter?.columnIds) {
            const finalFilters = cloned.filter(f => !dualFilter.columnIds.includes(f.columnId));
            pendingUserFiltersRef.current = finalFilters;
            setCurrentFilters(finalFilters);
            onFilterBy(finalFilters);
          }
        }
      }
    }
  };

  /**
   * Adds or update a filter
   * @param {string} condition Filter condition
   * @param {string | date | number} value Filter value
   * @returns
   */
  const handleChangeFilters = (filters: Array<TableFilterItem>) => {
    let cloned = [...currentFilters];

    // Check if we're dealing with dual-multiselect filters
    // For dual-multiselect, the filter's columnId might be one of the three columnIds,
    // not necessarily the main column id
    const isDualMultiselect = filters.some(f => columns.some(col =>
        col.filters?.some(
          filter => filter.type === 'dual-multiselect' && filter.columnIds?.includes(f.columnId),
        ),
      ));

    // For dual-multiselect, clear all related columnIds first
    if (isDualMultiselect) {
      const allDualColumnIds = new Set<string>();
      filters.forEach(f => {
        const column = columns.find(col =>
          col.filters?.some(
            filter => filter.type === 'dual-multiselect' && filter.columnIds?.includes(f.columnId),
          ),
        );
        if (column) {
          const dualFilter = column.filters?.find(filter => filter.type === 'dual-multiselect');
          dualFilter?.columnIds?.forEach(id => allDualColumnIds.add(id));
        }
      });

      // Remove all filters with these columnIds
      cloned = cloned.filter(f => !allDualColumnIds.has(f.columnId));
    } else {
      const incomingColumnIds = new Set<string>();

      filters.forEach(filter => {
        const column = columns.find(
          col => col.id === filter.columnId || col.alias === filter.columnId,
        );

        if (column) {
          incomingColumnIds.add(column.id);

          if (column.alias) {
            incomingColumnIds.add(column.alias);
          }
        } else {
          incomingColumnIds.add(filter.columnId);
        }
      });

      cloned = cloned.filter(filter => !incomingColumnIds.has(filter.columnId));
    }

    filters.forEach(filter => {
      const filterIndex = cloned.findIndex(
        f =>
          (f.columnId === filter.columnId || f.columnId === filter.alias) &&
          f.condition === filter.condition,
      );
      const filterValue = filter.value.toISOString?.() || filter.value;
      if (filterIndex !== -1) {
        if (filterValue) {
          cloned[filterIndex] = { ...filter, value: filterValue };
        } else {
          cloned.splice(filterIndex, 1);
        }
      } else {
        if (filterValue) {
          cloned.push({ ...filter, value: filterValue });
        }
      }
    });

    pendingUserFiltersRef.current = cloned;
    setCurrentFilters(cloned);
    onFilterBy(cloned);
    setOpen(false);
  };

  const ordersMapToArray = ordersMap =>
    Array.from(ordersMap, ([key, value]) => ({ columnId: key, order: value }));

  const createSortHandler = property => () => {
    const newOrders = new Map();
    const currentOrder = appliedOrders.get(property);

    if (currentOrder === 'asc') {
      newOrders.set(property, 'desc');
    } else if (!currentOrder || currentOrder === 'desc') {
      newOrders.set(property, 'asc');
    }

    appliedOrders.forEach((value, key) => {
      if (key !== property) {
        newOrders.set(key, value);
      }
    });

    setAppliedOrders(newOrders);

    onRequestSort(ordersMapToArray(newOrders));
  };

  const renderSearchInput = () => (
    <Input
      data-testid="search-input"
      endAdornment={
        <InputAdornment position="end">
          <IconButton
            data-testid="search-input-button"
            disableRipple
            onClick={() => {
              onSearch(searchText);
            }}
            size="small"
          >
            <SearchIcon />
          </IconButton>
        </InputAdornment>
      }
      onChange={e => {
        setSearchText(e.target.value);
      }}
      onKeyPress={e => {
        if (e.key === 'Enter') {
          onSearch(searchText);
        }
      }}
      value={searchText}
    />
  );

  const renderAutocomplete = () => (
    <Autocomplete
      data-testid="search-autocomplete"
      id="combo-box-demo"
      options={suggestions}
      renderInput={params => (
        <TextField
          {...params}
          className={classes.autocomplete}
          onChange={e => {
            setSearchText(e.target.value);
          }}
          onKeyPress={e => {
            if (e.key === 'Enter') {
              onSearch(searchText);
            }
          }}
        />
      )}
    />
  );
  function loadColumnInfoLocalStorage() {
    let columnsInfo = localStorage.getItem(`${localStorageKey}-columnsInfo`);
    if (columnsInfo) {
      columnsInfo = JSON.parse(columnsInfo);
      Object.keys(columnsInfo).forEach((colField, index) => {
        if (columnRefs[index]) {
          columnRefs[index].current.parentElement.style.width = columnsInfo[colField];
        }
      });
    }
  }

  const saveColumnInfoLocalStorage = () => {
    const columnsInfo = {};
    columns.forEach((col, index) => {
      if (columnRefs[index]) {
        columnsInfo[col.label] = {};
        columnsInfo[col.label] = columnRefs[index].current?.parentElement?.style.width;
      }
    });
    localStorage.setItem(`${localStorageKey}-columnsInfo`, JSON.stringify(columnsInfo));
  };

  const adjustWidthColumn = (index, width) => {
    const minWidth = DEFAULT_MIN_WIDTH_CELL;
    const maxWidth = DEFAULT_MAX_WIDTH_CELL;
    const newWidth = width > maxWidth ? maxWidth : width < minWidth ? minWidth : width;

    setColumnRefs(currentState => {
      const localCurrentState = [...currentState];
      if (localCurrentState[index]) {
        localCurrentState[index].current.parentElement.style.width = newWidth + 'px';
      }

      return localCurrentState;
    });
  };

  const setCursorDocument = isResizing => {
    document.body.style.cursor = isResizing ? 'col-resize' : 'auto';
  };

  const handleOnMouseMove = e => {
    if (columnRefs[isResizing.current]) {
      if (isResizing.current >= 0) {
        const newWidth =
          e.clientX -
          columnRefs[isResizing.current].current.parentElement?.getBoundingClientRect().left;
        adjustWidthColumn(isResizing.current, newWidth);
      }
    }
  };

  const handleOnMouseUp = () => {
    isResizing.current = -1;
    saveColumnInfoLocalStorage();
    setCursorDocument(false);
  };

  const onClickResizeColumn = (event, index) => {
    event.preventDefault();
    event.stopPropagation();
    isResizing.current = index;
    setCursorDocument(true);
  };

  const getFilterChipValue = (column, value) => {
    const filterFormattedValue = column.filterFormatter?.(value);
    if (typeof filterFormattedValue === 'string' || typeof filterFormattedValue === 'number') {
      return filterFormattedValue;
    }

    // Cell formatters often need the row and may return JSX or an empty
    // placeholder when called with only the filter value (e.g. Name columns).
    // Only use non-empty string/number results for the chip label.
    const formattedValue = column.formatter?.(value);
    if (
      (typeof formattedValue === 'string' && formattedValue !== '') ||
      typeof formattedValue === 'number'
    ) {
      return formattedValue;
    }

    return value;
  };

  return (
    <MuiTableHead>
      {searchType ? (
        <TableRow>
          <TableCell
            className={clsx(classes.padding, classes.searchRow)}
            colSpan={actionCell ? columns.length + 1 : columns.length}
          >
            {searchType === 'input' ? renderSearchInput() : null}
            {searchType === 'autocomplete' ? renderAutocomplete() : null}
          </TableCell>
        </TableRow>
      ) : null}
      <TableRow>
        {actionCell && <TableCell className={clsx(classes.padding, classes.actionCell)} />}
        {columns.map((column, index) => {
          const currentOrder = appliedOrders.get(column.id);
          return (
            <TableCell
              key={column.id + index}
              align={column.align || AlignTypes.LEFT}
              className={clsx(
                classes.padding,
                classes.headerCell,
                index === 0 && classes.firstColumn,
              )}
              sortDirection={currentOrder ? currentOrder : false}
            >
              {column.sortable ? (
                <TableSortLabel
                  data-testid={`${column.label}-sorter`}
                  active={!!currentOrder}
                  classes={{
                    root: classes.sortLabel,
                    icon: classes.sortIcon,
                  }}
                  direction={currentOrder ? currentOrder : 'asc'}
                  onClick={createSortHandler(column.id)}
                >
                  <span>{column.label}</span>
                  {currentOrder ? (
                    <span className={classes.visuallyHidden}>
                      {currentOrder === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </span>
                  ) : null}
                </TableSortLabel>
              ) : (
                <InputLabel className={classes.label}>{column.label}</InputLabel>
              )}
              {column.filters ? (
                <IconButton
                  data-testid={`${column.label}-filter`}
                  className={classes.filterIcon}
                  disableRipple
                  size="small"
                  ref={column => (anchorRefs.current[index] = column)}
                  onClick={() => {
                    setActiveColumn(column);
                    setOpen(true);
                  }}
                >
                  <FilterIcon />
                </IconButton>
              ) : null}
              {column.control}
              <div
                className={classes.resizeLine}
                onMouseDown={event => onClickResizeColumn(event, index)}
                ref={columnRefs[index]}
              />
            </TableCell>
          );
        })}
      </TableRow>
      {currentFilters.length ? (
        <TableRow>
          <TableCell
            className={classes.filterCell}
            colSpan={actionCell ? columns.length + 1 : columns.length}
          >
            {currentFilters.map(({ columnId, condition, value }) => {
              // First try to find column by direct match
              let column = columns.find(item => item.id === columnId || item.alias === columnId);

              // If not found, check if this columnId is part of a dual-multiselect filter
              if (!column) {
                column = columns.find(item =>
                  item.filters?.some(
                    filter =>
                      filter.type === 'dual-multiselect' && filter.columnIds?.includes(columnId),
                  ),
                );
              }

              if (!column) {
                return null;
              }

              const filter = column.filters?.find(
                filter => filter.conditions[0]?.key === condition,
              );

              // For dual-multiselect, provide a custom label based on which columnId is used
              let filterLabel = filter?.label || condition;
              if (filter?.type === 'dual-multiselect' && filter.columnIds) {
                const [leftOnlyId, rightOnlyId, bothSelectedId] = filter.columnIds;
                if (columnId === leftOnlyId) {
                  filterLabel = 'Source';
                } else if (columnId === rightOnlyId) {
                  filterLabel = 'Type';
                } else if (columnId === bothSelectedId) {
                  filterLabel = 'Source + Type';
                }
              } else if (!filter) {
                // If filter not found, try to detect dual-multiselect by checking column filters
                const dualFilter = column.filters?.find(f => f.type === 'dual-multiselect');
                if (dualFilter?.columnIds) {
                  const [leftOnlyId, rightOnlyId, bothSelectedId] = dualFilter.columnIds;
                  if (columnId === leftOnlyId) {
                    filterLabel = 'Source';
                  } else if (columnId === rightOnlyId) {
                    filterLabel = 'Type';
                  } else if (columnId === bothSelectedId) {
                    filterLabel = 'Source + Type';
                  }
                }
              }

              return (
                <Chip
                  className={classes.filterChip}
                  key={columnId + condition + value}
                  label={`${column.label} ${filterLabel}: ${getFilterChipValue(column, value)}`}
                  onDelete={() => onDiscardFilter(columnId, condition)}
                />
              );
            })}
          </TableCell>
        </TableRow>
      ) : null}
      {activeColumn && (
        <ColumnFilterPopper
          open={open}
          anchorEl={anchorRefs.current[columns.findIndex(col => col.id === activeColumn.id)]}
          column={activeColumn}
          activeFilters={currentFilters.filter(f => {
            // Direct match
            if (f.columnId === activeColumn.id || f.columnId === activeColumn.alias) {
              return true;
            }
            // Check if this filter belongs to a dual-multiselect in this column
            const dualMultiselectFilter = activeColumn.filters?.find(
              filter => filter.type === 'dual-multiselect',
            );
            if (dualMultiselectFilter?.columnIds) {
              return dualMultiselectFilter.columnIds.includes(f.columnId);
            }
            return false;
          })}
          onClose={handleClose}
          onFilter={handleChangeFilters}
        />
      )}
    </MuiTableHead>
  );
};
